import CreativeFileUpload from './CreativeFileUpload';
import AdvancedDocumentEditor from './AdvancedDocumentEditor';
import React, { useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

// Configurar o worker do PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const { TextField, Select, InputLabel, FormControl, MenuItem, FormHelperText } = require('@mui/material');
const { TitleForm, ButtonForm } = require('Components/Form/styles');
const { Row, Column } = require('Components/Grid');
const { Controller } = require('react-hook-form');
const { useQuery } = require('react-query');
const { useSelector } = require('react-redux');
const { getUsersNameEmailByCompany } = require('services/users-service');
const { useAuthTenant, useAuthUser } = require('store/auth');
const { getSelectedObject } = require('utils/select_utils');
const { default: PolicyEditor } = require('./Editor');
const { default: DotsLoader } = require('Components/DotsLoader');

export const VersionFormComponent = ({
  control,
  errors,
  getValues,
  handleBack,
  handleComplete,
  handleUpdateAttachment,
  handleSubmit,
  id,
  isLoading,
  register,
  setValue,
  existingData,
}) => {
  const selectedTenant = useSelector(useAuthTenant);
  const user = useSelector(useAuthUser);
  const SwalReact = withReactContent(Swal);

  const { data: users } = useQuery(['getUsers'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getUsersNameEmailByCompany(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const getDefaultAuthor = (author, arr) => {
    if (!id) {
      const r = arr?.find(f => f?.email === author?.email);
      return r || '';
    }
    return '';
  };

  const [inputType, setInputType] = useState('editor'); // 'editor' | 'upload'
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false); // trava anti-duplo clique
  
  // Estados para edição de documento
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  const [editingText, setEditingText] = useState('');
  const [isChangingDocument, setIsChangingDocument] = useState(false);
  const [newDocument, setNewDocument] = useState(null);
  const [showAdvancedEditor, setShowAdvancedEditor] = useState(false);

  // Verificar se está em modo de edição (existingData existe)
  const isEditMode = existingData && existingData.attachment;

  const pickId = (v) => (typeof v === 'string' ? v : (v?._id || v?.id || ''));

  // Função para salvar conteúdo do editor avançado
  const handleSaveAdvancedEdit = (newContent) => {
    // Atualizar o texto da política com o novo conteúdo
    setValue('policyText', newContent);
    
    SwalReact.fire({
      title: 'Sucesso!',
      text: 'Documento editado e salvo com sucesso! As alterações serão aplicadas quando você salvar o formulário.',
      icon: 'success',
      confirmButtonText: 'OK'
    });
  };

  // Função para fechar o editor avançado
  const handleCloseAdvancedEditor = () => {
    setShowAdvancedEditor(false);
  };

  // Função para salvar as alterações do documento
  const handleSaveDocumentChanges = () => {
    SwalReact.fire({
      title: 'Salvar alterações?',
      text: 'Deseja salvar as alterações feitas no documento?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, salvar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        // Aqui você pode implementar a lógica para salvar as alterações
        // Por exemplo, substituir o texto da política pelo texto editado
        SwalReact.fire({
          title: 'Sucesso!',
          text: 'Alterações salvas com sucesso!',
          icon: 'success',
          confirmButtonText: 'OK'
        }).then(() => {
          setIsEditingDocument(false);
          setEditingText('');
        });
      }
    });
  };

  // Função para cancelar a edição
  const handleCancelEdit = () => {
    SwalReact.fire({
      title: 'Cancelar edição?',
      text: 'Todas as alterações não salvas serão perdidas.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, cancelar',
      cancelButtonText: 'Continuar editando'
    }).then((result) => {
      if (result.isConfirmed) {
        setIsEditingDocument(false);
        setEditingText('');
      }
    });
  };

  // Função para trocar documento
  const handleChangeDocument = () => {
    setIsChangingDocument(true);
  };

  // Função para cancelar troca de documento
  const handleCancelChangeDocument = () => {
    setIsChangingDocument(false);
    setNewDocument(null);
  };

  // Função para confirmar nova documento
  const handleConfirmNewDocument = async () => {
    if (!newDocument) {
      SwalReact.fire({
        title: 'Atenção!',
        text: 'Selecione um arquivo antes de confirmar.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    const result = await SwalReact.fire({
      title: 'Confirmar troca?',
      text: 'O documento atual será substituído pelo novo arquivo selecionado. Esta ação será salva imediatamente.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, trocar e salvar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        // Mostrar loading
        SwalReact.fire({
          title: 'Salvando...',
          text: 'Substituindo documento...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          willOpen: () => {
            SwalReact.showLoading();
          }
        });

        // Se é uma edição de versão existente, usar função específica para atualizar apenas o anexo
        if (existingData && handleUpdateAttachment) {
          await handleUpdateAttachment(newDocument);
        } else {
          // Se é uma nova versão, atualizar o anexo no formulário e salvar tudo
          // Preparar payload para salvar imediatamente
          const form = getValues();
          const normalizedPayload = {
            version: String(form.version || '').trim(),
            status: form.status,
            companyKey: selectedTenant?.slug || form.companyKey || '',
            authorId: pickId(form.author),
            reviewerId: pickId(form.reviewer),
            approverId: pickId(form.approver),
            policyText: form.policyText || existingData?.policyText || '',
            attachment: newDocument, // Novo documento selecionado
          };

          // Salvar imediatamente via handleComplete
          await handleComplete?.(normalizedPayload);
        }
        
        SwalReact.fire({
          title: 'Sucesso!',
          text: 'Documento trocado e salvo com sucesso!',
          icon: 'success',
          confirmButtonText: 'OK'
        }).then(() => {
          setIsChangingDocument(false);
          setNewDocument(null);
          // Recarregar a página para mostrar as alterações
          window.location.reload();
        });

      } catch (error) {
        console.error('Erro ao trocar documento:', error);
        SwalReact.fire({
          title: 'Erro!',
          text: 'Erro ao salvar o novo documento. Tente novamente.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const onSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const form = getValues();
      // normaliza TUDO aqui (inclusive _id -> string)
      const normalizedPayload = {
        version: String(form.version || '').trim(),
        status: form.status,
        companyKey: selectedTenant?.slug || form.companyKey || '',
        authorId:   pickId(form.author),
        reviewerId: pickId(form.reviewer),
        approverId: pickId(form.approver),
        policyText: isEditMode ? (existingData.policyText || '') : (inputType === 'editor' ? (form.policyText || '') : ''),
        attachment: isEditMode ? (form.attachment || existingData.attachment) : (inputType === 'upload' ? form.attachment : null), // precisa ser File quando upload
      };

      await handleComplete?.(normalizedPayload);
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const getAuthorValue = (value) =>
    getSelectedObject(users, value) ?? getDefaultAuthor(user, users) ?? '';

  return (
    // Sem <form>; evita submit nativo (Enter) apenas em inputs, não no editor
    <div onKeyDown={(e) => { 
      if (e.key === 'Enter' && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
        e.preventDefault();
      }
    }}>
      <Row justifyContent="center">
        <Column xs={6} className="d-flex">
          <TextField
            {...register('version', { required: 'Versão é obrigatório' })}
            label="Versão"
            autoFocus
            error={!!errors?.version}
            sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
            helperText={errors?.version ? errors?.version.message : null}
            InputLabelProps={{ shrink: true }}
          />
        </Column>

        <Column xs={6} className="d-flex">
          <Controller
            control={control}
            rules={{ validate: () => getValues('author') !== '', required: 'Autor é obrigatório' }}
            name="author"
            render={({ field: { onChange, value }, fieldState }) => (
              <FormControl fullWidth sx={{ marginBottom: '24px' }} error={!!fieldState.error}>
                <InputLabel id="autor" shrink={true}>Autor</InputLabel>
                <Select
                  labelId="autor"
                  id="autor"
                  value={getAuthorValue(value)}
                  notched
                  label="Autor"
                  onChange={(e) => onChange(e.target.value)}
                >
                  <MenuItem value={''} key="author--1">Selecione um autor</MenuItem>
                  {users?.map((item, i) => (
                    <MenuItem value={item} key={`author-${i}`}>{item.name}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
              </FormControl>
            )}
          />
        </Column>
      </Row>

      <Row>
        <Column xs={6} className="d-flex">
          <Controller
            control={control}
            rules={{ validate: () => getValues('reviewer') !== '', required: 'Revisor é obrigatório' }}
            name="reviewer"
            render={({ field: { onChange, value }, fieldState }) => (
              <FormControl fullWidth sx={{ marginBottom: '24px' }} error={!!fieldState.error}>
                <InputLabel id="revisor" shrink={true}>Revisor</InputLabel>
                <Select
                  labelId="revisor"
                  id="revisor"
                  value={getSelectedObject(users, value) ?? ''}
                  notched
                  label="Revisor"
                  onChange={(e) => onChange(e.target.value)}
                >
                  <MenuItem value="" key="reviewer--1">Selecione um revisor</MenuItem>
                  {users?.map((item, i) => (
                    <MenuItem value={item} key={`reviewer-${i}`}>{item.name}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
              </FormControl>
            )}
          />
        </Column>

        <Column xs={6} className="d-flex">
          <Controller
            control={control}
            rules={{ validate: () => getValues('approver') !== '', required: 'Aprovador é obrigatório' }}
            name="approver"
            render={({ field: { onChange, value }, fieldState }) => (
              <FormControl fullWidth sx={{ marginBottom: '24px' }} error={!!fieldState.error}>
                <InputLabel id="aprovador" shrink={true}>Aprovador</InputLabel>
                <Select
                  labelId="aprovador"
                  id="aprovador"
                  value={getSelectedObject(users, value) ?? ''}
                  notched
                  label="Aprovador"
                  onChange={(e) => onChange(e.target.value)}
                >
                  <MenuItem value="" key="approver--1">Selecione um aprovador</MenuItem>
                  {users?.map((item, i) => (
                    <MenuItem value={item} key={`approver-${i}`}>{item.name}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
              </FormControl>
            )}
          />
        </Column>
      </Row>

      <Row>
        <Column xs={6} className="d-flex">
          <Controller
            control={control}
            rules={{ validate: () => getValues('status') !== '', required: 'Status é obrigatório' }}
            name="status"
            render={({ field: { onChange, value }, fieldState }) => (
              <FormControl fullWidth sx={{ marginBottom: '24px' }} error={!!fieldState.error}>
                <InputLabel id="status" shrink={true}>Status</InputLabel>
                <Select
                  labelId="status"
                  id="status"
                  value={value ?? ''}
                  notched
                  label="Status"
                  onChange={(e) => onChange(e.target.value)}
                >
                  <MenuItem value="" key="status--1">Selecione um status</MenuItem>
                  {['Iniciado', 'Concluído'].map((item, i) => (
                    <MenuItem value={item} key={`status-${i}`}>{item}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
              </FormControl>
            )}
          />
        </Column>
      </Row>

      {/* Toggle Editor/Upload - só mostra se não estiver editando */}
      {!isEditMode && (
        <Row>
          <Column xs={12}>
            <div style={{ display: 'flex', gap: 12, margin: '24px 0' }}>
              <ButtonForm
                type="button"
                variant={inputType === 'editor' ? 'contained' : 'outlined'}
                onClick={() => setInputType('editor')}
                gray={inputType !== 'editor'}
                style={{ fontWeight: inputType === 'editor' ? 700 : 400 }}
                label="Usar Editor"
              />
              <ButtonForm
                type="button"
                variant={inputType === 'upload' ? 'contained' : 'outlined'}
                onClick={() => setInputType('upload')}
                gray={inputType !== 'upload'}
                style={{ fontWeight: inputType === 'upload' ? 700 : 400 }}
                label="Fazer Upload"
              />
            </div>
          </Column>
        </Row>
      )}

      {/* Renderização condicional - só mostra se não estiver editando */}
      {!isEditMode && inputType === 'editor' && (
        <Row>
          <Column>
            <Controller
              control={control}
              name="policyText"
              rules={{
                validate: () => (inputType === 'editor'
                  ? !!getValues('policyText') || 'Preencha o texto da versão.'
                  : true),
              }}
              render={({ field: { onChange, value }, fieldState }) => (
                <FormControl
                  fullWidth
                  sx={{
                    margin: '0 auto px',
                    '& .ql-picker-label > svg': { height: '18px', width: '18px' },
                    '& .ql-container.ql-snow': { minHeight: '200px' },
                  }}
                  error={!!fieldState.error}
                >
                  <Row><TitleForm>Texto da Versão</TitleForm></Row>
                  <PolicyEditor value={value} onChange={onChange} />
                  <FormHelperText>{fieldState.error ? fieldState.error.message : ''}</FormHelperText>
                </FormControl>
              )}
            />
          </Column>
        </Row>
      )}

      {!isEditMode && inputType === 'upload' && (
        <Controller
          control={control}
          name="attachment"
          rules={{
            validate: () => (inputType === 'upload'
              ? !!getValues('attachment') || 'Selecione um arquivo.'
              : true),
          }}
          render={({ field: { onChange, value }, fieldState }) => (
            <CreativeFileUpload
              id="attachment-upload"
              value={value}
              onChange={onChange}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              helperText={fieldState.error ? fieldState.error.message : ''}
              error={!!fieldState.error}
            />
          )}
        />
      )}

      {/* Seção de edição de documento existente - aparece só no modo de edição */}
      {id && existingData?.attachment && (
        <Row>
          <Column xs={12}>
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px', 
              padding: '20px', 
              marginBottom: '24px',
              backgroundColor: '#f9f9f9'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                    Documento Anexado: {existingData.attachment.originalName}
                  </h4>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                    {existingData.attachment.mimetype} • {Math.round(existingData.attachment.size / 1024)} KB
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <ButtonForm
                    type="button"
                    variant="button"
                    label="Trocar Documento"
                    onClick={handleChangeDocument}
                    disabled={isEditingDocument || isChangingDocument}
                  />
                </div>
              </div>

              {/* Modo de edição do documento */}
              {isEditingDocument && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h5 style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>
                      Editando conteúdo do documento:
                    </h5>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <ButtonForm
                        type="button"
                        primary
                        label="Salvar Alterações"
                        onClick={handleSaveDocumentChanges}
                        size="small"
                      />
                      <ButtonForm
                        type="button"
                        secondary
                        label="Cancelar"
                        onClick={handleCancelEdit}
                        size="small"
                      />
                    </div>
                  </div>
                  <TextField
                    multiline
                    rows={20}
                    fullWidth
                    variant="outlined"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    placeholder="Texto do documento aparecerá aqui para edição..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontFamily: 'Arial, sans-serif',
                        fontSize: '14px',
                        lineHeight: 1.6,
                        backgroundColor: 'white'
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </Column>
        </Row>
      )}

      {/* Seção de troca de documento */}
      {isChangingDocument && (
        <Row>
          <Column xs={12}>
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px', 
              padding: '20px', 
              marginBottom: '24px',
              backgroundColor: '#f0f8ff'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                  Trocar Documento
                </h4>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                  Selecione um novo arquivo para substituir o documento atual
                </p>
              </div>

              <CreativeFileUpload
                id="new-document-upload"
                value={newDocument}
                onChange={setNewDocument}
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                helperText="Selecione um arquivo para substituir o documento atual"
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                <ButtonForm
                  type="button"
                  secondary
                  label="Cancelar"
                  onClick={handleCancelChangeDocument}
                  size="small"
                />
                <ButtonForm
                  type="button"
                  primary
                  label="Confirmar Troca"
                  onClick={handleConfirmNewDocument}
                  disabled={!newDocument}
                  size="small"
                />
              </div>
            </div>
          </Column>
        </Row>
      )}

      <Row justifyContent="end" alignItems="center" className="mt-3">
        <Column xs={12}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <ButtonForm
              type="button"
              secondary
              id="cancel"
              label="cancelar"
              onClick={handleBack}
              disabled={isLoading || submitting}
            />
            <ButtonForm
              id="save"
              type="button"
              onClick={handleSubmit(onSubmit)}   // valida com RHF, depois chama onSubmit
              label={isLoading || submitting ? <DotsLoader /> : 'Salvar'}
              disabled={isLoading || submitting}
            />
          </div>
        </Column>
      </Row>

      {/* Editor Avançado de Documento */}
      {showAdvancedEditor && (
        <AdvancedDocumentEditor
          value={getValues('policyText') || ''}
          onChange={handleSaveAdvancedEdit}
          onClose={handleCloseAdvancedEditor}
          pdfUrl={existingData?.attachment?.url}
          title={`Editor Avançado - ${existingData?.attachment?.originalName || 'Documento'}`}
        />
      )}
    </div>
  );
};
